import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ArrowLeft, Building2, Save, Users, Calendar, User as UserIcon, Shield } from 'lucide-react';
import { toast } from 'sonner';
import Header from './Header';

const API = '/api';

const WorkspaceSettings = () => {
  const navigate = useNavigate();
  const { user, currentWorkspace, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [workspaceName, setWorkspaceName] = useState('');

  // Stable identity so the mount effect can include it in its deps and
  // satisfy react-hooks/exhaustive-deps without any suppression. The
  // function reads the token fresh from localStorage on each call.
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/workspace/settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSettings(response.data);
      setWorkspaceName(response.data.workspace.name);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load workspace settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleUpdateWorkspace = async (e) => {
    e.preventDefault();
    
    if (!workspaceName.trim()) {
      toast.error('Workspace name cannot be empty');
      return;
    }

    setSaving(true);
    
    try {
      await axios.put(
        `${API}/workspace/settings`,
        { name: workspaceName.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Workspace updated successfully');
      
      // Update current workspace in localStorage and context
      const updatedWorkspace = { ...currentWorkspace, name: workspaceName.trim() };
      localStorage.setItem('current_workspace', JSON.stringify(updatedWorkspace));
      
      // Refresh to update workspace name everywhere
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update workspace');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  const canEdit = settings?.user_role === 'owner' || settings?.user_role === 'admin';

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Workspace Settings</h1>
          <p className="text-slate-600">Manage your workspace configuration and details</p>
        </div>

        <div className="space-y-6">
          {/* Workspace Overview Card */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-navy-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-navy-600" />
                <span>Workspace Overview</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-600 text-sm">Workspace ID</Label>
                  <p className="text-sm font-mono text-slate-900 bg-white px-3 py-2 rounded border mt-1">
                    {settings?.workspace?.id}
                  </p>
                </div>
                
                <div>
                  <Label className="text-slate-600 text-sm">Your Role</Label>
                  <div className="mt-1">
                    <Badge className="capitalize bg-emerald-100 text-navy-700 border-emerald-200">
                      <Shield className="w-3 h-3 mr-1" />
                      {settings?.user_role?.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-600 text-sm">Created On</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <p className="text-sm text-slate-900">
                      {formatDate(settings?.workspace?.created_at)}
                    </p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-600 text-sm">Created By</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <UserIcon className="w-4 h-4 text-slate-500" />
                    <p className="text-sm text-slate-900">
                      {settings?.creator?.full_name || settings?.creator?.email || 'Unknown'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-600 text-sm">Active Members</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Users className="w-4 h-4 text-slate-500" />
                    <p className="text-sm text-slate-900">
                      {settings?.member_count || 0} member{settings?.member_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        
          {/* Workspace Information */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-navy-600" />
                <span>Workspace Information</span>
              </CardTitle>
              <CardDescription>
                {canEdit 
                  ? 'Update your workspace name and details'
                  : 'View workspace information (read-only)'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateWorkspace} className="space-y-4">
                <div>
                  <Label htmlFor="workspace_name">Workspace Name</Label>
                  <Input
                    id="workspace_name"
                    type="text"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    placeholder="Enter workspace name"
                    required
                    disabled={!canEdit}
                    className={!canEdit ? 'bg-slate-100' : ''}
                  />
                  {!canEdit && (
                    <p className="text-xs text-slate-500 mt-1">
                      Only owners and admins can update workspace settings
                    </p>
                  )}
                </div>

                {canEdit && (
                  <Button
                    type="submit"
                    disabled={saving || workspaceName.trim() === settings?.workspace?.name}
                    className="bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Future Settings Placeholder */}
          <Card className="border-0 shadow-lg border-dashed border-2 border-slate-200 bg-slate-50">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">More Settings Coming Soon</h3>
              <p className="text-slate-500 text-sm">
                Additional workspace management features will be available here, including:
              </p>
              <ul className="text-sm text-slate-500 mt-2 space-y-1">
                <li>• Domain settings</li>
                <li>• Member management</li>
                <li>• Billing and subscription</li>
                <li>• Integrations</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSettings;
